import React from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import tableStyles from '../../../components/tables/table.module.scss';
// Namerno delimo stilove sa tabelom terapija - prikaz mesecne aktivnosti je isti.
import styles from './TherapiesExpandableTable.module.scss';
import StatusFlag from '../../../components/elements/StatusFlag';
import { getOccurrenceStatusTone } from '../../../shared/utils/statusTones';
import { getOccurrenceStatusDisplayLabel } from '../utils/therapyOccurrencePresentation';
import { getTherapyStatusLabel } from '../utils/therapyPresentation';

// Napomena moze da bude duza od jednog reda, pa se aktivnosti crtaju red po red
// (a ne kolona po kolona) kako bi visine celija ostale poravnate.
const noteCellStyle = { whiteSpace: 'normal', textAlign: 'left' };

const getMedicationsLabel = (medications) =>
  Array.isArray(medications) && medications.length
    ? medications.join(', ')
    : '-';

const getMedicationsShortLabel = (medications) =>
  Array.isArray(medications) && medications.length
    ? medications.map((medication) => medication.split(' ')[0]).join(', ')
    : '-';

const SavedTherapiesTable = ({
  data = [],
  onPatientClick,
  emptyState = {
    icon: '/assets/icons/no-users.svg',
    title: 'Trenutno nema sacuvanih terapija',
  },
  tableMinWidth = 1250,
}) => (
  <div className={styles.tableWrapper}>
    <table
      style={{ width: '100%', minWidth: tableMinWidth ?? undefined }}
      className={styles.therapiesTable}
    >
      <colgroup>
        <col style={{ width: '11%' }} />
        <col style={{ width: '11%' }} />
        <col style={{ width: '10%' }} />
        <col style={{ width: '9%' }} />
        <col style={{ width: '9%' }} />
        <col style={{ width: '12%' }} />
        <col style={{ width: '6%' }} />
        <col style={{ width: '10%' }} />
        <col style={{ width: '10%' }} />
        <col style={{ width: '12%' }} />
      </colgroup>
      <thead>
        <tr>
          <th rowSpan={2}>Prezime</th>
          <th rowSpan={2}>Ime</th>
          <th rowSpan={2}>LBO</th>
          <th rowSpan={2}>BZK</th>
          <th rowSpan={2}>Preuzimanje</th>
          <th rowSpan={2}>Lekovi</th>
          <th colSpan={4} className={styles.groupHeader}>
            {'Mesečna aktivnost'}
          </th>
        </tr>
        <tr>
          <th className={styles.subHeader}>Mesec</th>
          <th className={styles.subHeader}>Datum</th>
          <th className={styles.subHeader}>Status</th>
          <th className={styles.subHeader}>Napomena</th>
        </tr>
      </thead>
      <tbody>
        {data.map((patient, index) => {
          const occurrences = Array.isArray(patient?.savedOccurrences)
            ? patient.savedOccurrences
            : [];
          const medicationsLabel = getMedicationsLabel(patient?.medications);

          return (
            <tr key={patient?.userId || index}>
              <td className={styles.topAlignedCell}>
                <span
                  className={styles.wrapText}
                  title={patient?.lastName || '-'}
                  role="button"
                  tabIndex={0}
                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={() => onPatientClick?.(patient)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      onPatientClick?.(patient);
                    }
                  }}
                >
                  {patient?.lastName || '-'}
                </span>
              </td>
              <td className={styles.topAlignedCell}>
                <span
                  className={styles.wrapText}
                  title={patient?.firstName || '-'}
                >
                  {patient?.firstName || '-'}
                </span>
              </td>
              <td className={styles.topAlignedCell}>
                <span
                  className={styles.wrapText}
                  title={patient?.userLbo || '-'}
                >
                  {patient?.userLbo || '-'}
                </span>
              </td>
              <td className={styles.topAlignedCell}>
                <span
                  className={styles.wrapText}
                  title={patient?.healthBookletNumber || '-'}
                >
                  {patient?.healthBookletNumber || '-'}
                </span>
              </td>
              <td
                className={`${styles.topAlignedCell} ${styles.activityCell}`}
                title={patient?.userPickupMethod || 'APO'}
              >
                <span className={styles.nowrapText}>
                  {patient?.userPickupMethod || 'APO'}
                </span>
              </td>
              <td className={`${styles.topAlignedCell} ${styles.activityCell}`}>
                <span className={styles.wrapText} title={medicationsLabel}>
                  {getMedicationsShortLabel(patient?.medications)}
                </span>
              </td>
              <td
                colSpan={4}
                className={`${styles.topAlignedCell} ${styles.activityGroupCell}`}
              >
                {occurrences.length ? (
                  <div className={styles.activityColumns}>
                    {occurrences.map((occurrence, occurrenceIndex) => {
                      const key = occurrence?._id || occurrenceIndex;
                      const statusLabel =
                        getOccurrenceStatusDisplayLabel(occurrence);
                      const therapyStatusLabel = getTherapyStatusLabel(
                        occurrence?.therapyStatus
                      );

                      return (
                        <React.Fragment key={key}>
                          <div className={styles.activityColumn}>
                            <div className={styles.activityItem}>
                              <span
                                className={styles.nowrapText}
                                style={{ textAlign: 'center' }}
                              >
                                {occurrence?.sequenceNumber || '-'}
                              </span>
                            </div>
                          </div>
                          <div className={styles.activityColumn}>
                            <div className={styles.activityItem}>
                              <span className={styles.nowrapText}>
                                {occurrence?.scheduledDate
                                  ? dayjs(occurrence.scheduledDate).format(
                                      'DD.MM.YY'
                                    )
                                  : '-'}
                              </span>
                            </div>
                          </div>
                          <div className={styles.activityColumn}>
                            <div className={styles.activityItem}>
                              <StatusFlag
                                tone={getOccurrenceStatusTone(occurrence?.status)}
                                title={[
                                  statusLabel,
                                  occurrence?.therapyName,
                                  occurrence?.therapyStatus
                                    ? `Terapija: ${therapyStatusLabel}`
                                    : null,
                                ]
                                  .filter(Boolean)
                                  .join(' - ')}
                              >
                                {statusLabel}
                              </StatusFlag>
                            </div>
                          </div>
                          <div
                            className={styles.activityColumn}
                            style={noteCellStyle}
                          >
                            <div
                              className={styles.activityItem}
                              style={noteCellStyle}
                            >
                              <span title={occurrence?.note || '-'}>
                                {occurrence?.note || '-'}
                              </span>
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </div>
                ) : (
                  <div className={styles.emptyActivity}>-</div>
                )}
              </td>
            </tr>
          );
        })}
        {data.length === 0 && (
          <tr>
            <td colSpan={10} style={{ textAlign: 'center', padding: 8 }}>
              <div className={tableStyles.noData}>
                <img src={emptyState.icon} alt="Nema podataka" />
                <h3>{emptyState.title}</h3>
              </div>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

SavedTherapiesTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  onPatientClick: PropTypes.func,
  emptyState: PropTypes.shape({
    icon: PropTypes.string,
    title: PropTypes.string,
  }),
  tableMinWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default SavedTherapiesTable;
